import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { AbsenceOverviewTable } from './components/AbsenceOverviewTable'
import { AbsenceExportDialog } from './components/AbsenceExportDialog'
import { ExportAbsencesButton } from './header-actions-components/ExportAbsencesButton'
import { AbsenceAllEntriesPage } from './all-entries-components/AbsenceAllEntriesPage'
import { AbsenceEmployeeEntriesPage } from './all-entries-components/AbsenceEmployeeEntriesPage'
import { useModuleSubView } from '../useModuleSubView'

interface Props {
  module: ServiceModuleModel
}

/**
 * Absence service module as its own page (card click → `?open`). The overview
 * (employee metrics + search + row dialogs) sits entirely in
 * `AbsenceOverviewTable`; the page only holds the header. The sub-view is
 * URL-driven (`useModuleSubView`, `?view=all` / `?view=employee&employeeId=…`),
 * so every view has a shareable URL and Back/Forward works: header button
 * "all absences" → `AbsenceAllEntriesPage`, row click → `AbsenceEmployeeEntriesPage`.
 */
export function AbsenceServiceModulePage({ module }: Props) {
  const { t } = useTranslation('service-modules-absence')
  const { view, showOverview, showAll, showEmployee } = useModuleSubView()
  const [exportAllOpen, setExportAllOpen] = useState(false)

  if (view.mode === 'all') {
    return <AbsenceAllEntriesPage module={module} onBack={showOverview} />
  }
  if (view.mode === 'employee') {
    return <AbsenceEmployeeEntriesPage module={module} employeeId={view.employeeId} onBack={showOverview} />
  }

  return (
    <PageLayout>
      <PageHeader title={module.name || t('absence.title')} description={t('absence.description')}>
        <PageHeaderActions>
          <ExportAbsencesButton onClick={() => setExportAllOpen(true)} />
          <Button onClick={showAll}>
            {t('absence.showAll')}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <AbsenceOverviewTable
        module={module}
        onRowClick={(employee) => employee.id && showEmployee(employee.id)}
      />

      <AbsenceExportDialog
        open={exportAllOpen}
        onOpenChange={setExportAllOpen}
        serviceModuleId={module.id}
      />
    </PageLayout>
  )
}
